import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DeviceContextService } from '../device-context.service';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import {
    formatInterfaceType,
    interfaceTypeTooltip,
    operationalStatusSeverity,
    formatOperationalStatus,
    formatNetworkSpeed,
    formatMacAddress,
} from '@/shared/network-interface-utils';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';

interface VersionRow {
    label: string;
    value: string | null | undefined;
}

interface VersionRowPair {
    left: VersionRow;
    right: VersionRow | null;
}

@Component({
    selector: 'app-appliance-status',
    standalone: true,
    imports: [CommonModule, TableModule, TagModule, TooltipModule, MessageModule, SectionCardComponent],
    templateUrl: './appliance-status.html',
    styleUrl: './appliance-status.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplianceStatus {
    private ctx = inject(DeviceContextService);
    deviceSig = this.ctx.device;

    get p() {
        return this.deviceSig()?.payload;
    }

    get applianceInfo() {
        return this.p?.applianceInfo;
    }

    versionRows = computed((): VersionRow[] => {
        const a = this.deviceSig()?.payload?.applianceInfo;
        if (!a) return [];
        return [
            { label: 'Model', value: a.model },
            { label: 'Model Name', value: a.modelName },
            { label: 'Firmware Version', value: a.firmwareVersion },
            { label: 'Firmware Build Date', value: a.firmwareBuildDate },
            { label: 'Firmware Build Hash', value: a.firmwareBuildHash },
            { label: 'Ethernet ID', value: a.ethernetId },
            { label: 'Serial Number', value: a.serialNumber },
            { label: 'Build', value: a.build },
            { label: 'Updater', value: a.updater },
            { label: 'Bootloader', value: a.bootloader },
            { label: 'CAB', value: a.cab },
            { label: 'Mono', value: a.mono },
            { label: '.NET', value: a.dotNet },
            { label: 'Python', value: a.python },
            { label: 'CCUI Version', value: a.ccuiVersion },
            { label: 'XIO SDK', value: a.xioSdk },
            { label: 'IoT SDK', value: a.iotSdk },
            { label: 'Build Time', value: a.buildTime },
            { label: 'Product ID', value: a.productId },
            { label: 'Revision ID', value: a.revisionId },
            { label: 'CPU ID', value: a.cpuId },
            { label: 'Ethernet PHY', value: a.ethernetPhy },
            { label: 'PUF', value: a.puf },
            { label: 'IOP Version', value: a.iopVersion },
            { label: 'Setup Program', value: a.setupProgram },
            { label: 'BACnet Version', value: a.bacnetVersion },
            { label: 'SNMP Version', value: a.snmpVersion },
            { label: 'Forced Auth Mode', value: a.forcedAuthMode },
            { label: 'FIPS Mode', value: a.fipsMode },
        ].filter(r => r.value != null && r.value !== '');
    });

    versionRowPairs = computed((): VersionRowPair[] => {
        const rows = this.versionRows();
        const pairs: VersionRowPair[] = [];

        for (let index = 0; index < rows.length; index += 2) {
            pairs.push({
                left: rows[index],
                right: rows[index + 1] ?? null,
            });
        }

        return pairs;
    });

    networkInterfaces = computed(() => {
        return this.deviceSig()?.payload?.network?.interfaces ?? [];
    });

    routingTable = computed(() => {
        return this.deviceSig()?.payload?.routingTable ?? [];
    });

    formatInterfaceType = formatInterfaceType;
    interfaceTypeTooltip = interfaceTypeTooltip;
    operationalStatusSeverity = operationalStatusSeverity;
    formatOperationalStatus = formatOperationalStatus;

    formatAddresses(addresses: string[] | null | undefined): string {
        if (!addresses?.length) return '—';
        return addresses.join(', ');
    }

    formatSpeed = formatNetworkSpeed;
    formatMacAddress = formatMacAddress;

    flagsTooltip(flags: string): string {
        const descriptions: Record<string, string> = {
            U: 'U — Route is up',
            G: 'G — Uses a gateway',
            H: 'H — Target is a host (not a network)',
            '!': '! — Reject route',
        };
        return [...(flags ?? '')].map(c => descriptions[c] ?? c).join('\n') || 'No flags';
    }
}
